import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n";

export default function Nav() {
  const { t, lang, toggle } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/gateways", label: t.nav.gateways },
    { to: "/investment", label: t.nav.investment },
    { to: "/network", label: t.nav.network },
    { to: "/about", label: t.nav.about },
  ];

  const dark = scrolled || open;

  return (
    <>
      <header
        data-testid="main-nav"
        className="fixed top-0 left-0 w-full z-50 transition-colors duration-500"
        style={{
          background: dark ? "rgba(6,32,41,0.92)" : "transparent",
          backdropFilter: dark ? "blur(16px)" : "none",
          borderBottom: dark ? "1px solid rgba(197,168,128,0.18)" : "1px solid transparent",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 text-[#F7F5F0]">
            <span className="font-serif-display text-2xl tracking-tight">SBG</span>
            <span className="hidden sm:block label text-[#C5A880] mt-1">Salvatore Business Gateway</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label}`}
                className={`link-underline font-body text-[13px] tracking-wide transition-opacity duration-300 ${
                  loc.pathname === l.to ? "text-[#C5A880]" : "text-[#F7F5F0]/85 hover:text-[#F7F5F0]"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="label text-[#F7F5F0]/90 hover:text-[#C5A880] transition-colors duration-300"
            >
              {lang === "de" ? "DE / EN" : "EN / DE"}
            </button>
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="hidden md:inline-block border border-[#C5A880]/60 text-[#F7F5F0] px-5 py-2.5 text-[12px] tracking-widest uppercase hover:bg-[#C5A880] hover:text-[#062029] transition-colors duration-500"
            >
              {t.nav.cta}
            </Link>
            <button
              className="lg:hidden text-[#F7F5F0]"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-[#062029] pt-24 px-8 lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-6">
              {links.concat([{ to: "/contact", label: t.nav.contact }]).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="font-serif-display text-4xl text-[#F7F5F0]"
                  data-testid={`mobile-nav-${l.label}`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
